import { ImageIcon, MinusIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import I18NLink from "@/components/i18n-link";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { formatNumberToCurrency } from "@/lib/utils";
import { getProductHandle } from "@/lib/storefront/catalog-filters";
import type { StorefrontCartLine } from "@/lib/storefront/cart-lines";
import { useCartSheet } from "@/hooks/storefront/use-cart-sheet";
import { useStorefrontCart } from "@/hooks/storefront/use-storefront-cart";
import type { CartItem } from "@/components/cart/types";

/** The cart, sliding in from the right of the header's cart button. */
export function StorefrontCartSheet() {
  const { t } = useTranslation("translation");
  const { isOpen, setOpen } = useCartSheet();
  const { lines, itemCount, subtotal, setQuantity, removeLine } =
    useStorefrontCart();
  const hasOrderableLines = lines.some((line) => !!line.product);

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={setOpen}>
      <DrawerContent className="text-foreground data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-md">
        <DrawerHeader className="flex flex-row items-center justify-between border-b px-4 py-3">
          <DrawerTitle className="text-xl">
            {t("Storefront.Cart.title", { count: itemCount })}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            {t("Storefront.Cart.description")}
          </DrawerDescription>
          <DrawerClose
            className="flex size-11 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
            aria-label={t("Storefront.Cart.close")}
          >
            <XIcon className="size-5" />
          </DrawerClose>
        </DrawerHeader>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <p className="text-lg font-medium">
              {t("Storefront.Cart.empty")}
            </p>
            <DrawerClose asChild>
              <I18NLink
                to="/"
                className="flex min-h-11 items-center rounded-full border px-6 font-medium hover:bg-gray-50"
              >
                {t("Storefront.Cart.continue_shopping")}
              </I18NLink>
            </DrawerClose>
          </div>
        ) : (
          <ul className="flex-1 divide-y overflow-y-auto px-4">
            {lines.map((line) => (
              <CartSheetLine
                key={line.item.id}
                line={line}
                onQuantityChange={setQuantity}
                onRemove={removeLine}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </ul>
        )}

        {lines.length > 0 && (
          <DrawerFooter className="gap-3 border-t p-4">
            <div className="flex items-center justify-between text-lg font-semibold">
              <span>{t("Storefront.Cart.subtotal")}</span>
              <span className="tabular-nums">
                {formatNumberToCurrency({ number: subtotal })}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {t("Storefront.Cart.shipping_note")}
            </p>
            {hasOrderableLines ? (
              <I18NLink
                to="/checkout"
                onClick={() => setOpen(false)}
                className="flex min-h-12 w-full items-center justify-center rounded-full bg-black px-6 text-center text-lg font-bold text-white transition-colors hover:bg-black/85"
              >
                {t("Storefront.Cart.checkout")}
              </I18NLink>
            ) : null}
            <DrawerClose className="min-h-11 w-full cursor-pointer rounded-full px-6 font-medium hover:bg-gray-100">
              {t("Storefront.Cart.continue_shopping")}
            </DrawerClose>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function CartSheetLine({
  line,
  onQuantityChange,
  onRemove,
  onNavigate,
}: {
  line: StorefrontCartLine;
  onQuantityChange: (item: CartItem, quantity: number) => void;
  onRemove: (item: CartItem) => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation("translation");
  const { item, product } = line;
  const image = product?.images[0];
  const atStockLimit =
    line.maxQuantity !== undefined && item.quantity >= line.maxQuantity;

  return (
    <li className="flex gap-3 py-4">
      <div className="size-20 shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {image ? (
          <img src={image} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-400">
            <ImageIcon className="size-6" />
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          {product ? (
            <I18NLink
              to={`/products/${getProductHandle(product)}`}
              onClick={onNavigate}
              className="line-clamp-2 font-medium hover:underline"
            >
              {item.name}
            </I18NLink>
          ) : (
            <span className="line-clamp-2 font-medium">{item.name}</span>
          )}
          {/* A line that left the shop is not charged, so it shows no price. */}
          {product && (
            <span className="shrink-0 font-semibold tabular-nums">
              {formatNumberToCurrency({ number: line.lineTotal })}
            </span>
          )}
        </div>
        {line.optionNames.length > 0 && (
          <p className="text-sm text-gray-500">{line.optionNames.join(", ")}</p>
        )}
        {!product && (
          <p className="text-sm font-medium text-red-600">
            {t("Storefront.Cart.no_longer_available")}
          </p>
        )}
        <div className="mt-1 flex items-center justify-between gap-2">
          {product ? (
            <div className="flex items-center rounded-full border">
              <button
                type="button"
                onClick={() => onQuantityChange(item, item.quantity - 1)}
                className="flex size-11 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
                aria-label={t("Storefront.Cart.decrease")}
              >
                <MinusIcon className="size-4" />
              </button>
              <span className="min-w-6 text-center font-semibold tabular-nums">
                {item.quantity}
              </span>
              <button
                type="button"
                disabled={atStockLimit}
                onClick={() => onQuantityChange(item, item.quantity + 1)}
                className="flex size-11 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={t("Storefront.Cart.increase")}
              >
                <PlusIcon className="size-4" />
              </button>
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => onRemove(item)}
            className="flex size-11 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
            aria-label={t("Storefront.Cart.remove", { name: item.name })}
          >
            <Trash2Icon className="size-4" />
          </button>
        </div>
        {atStockLimit && (
          <p className="text-xs text-gray-500">
            {t("Storefront.Cart.stock_limit", { count: line.maxQuantity })}
          </p>
        )}
      </div>
    </li>
  );
}
